import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ApiTeamPost201Response, UserResponseLoginMethodEnum } from './client';
import { useUserContext } from './state/UserContext';
import { useApi } from './state/ApiContext';
import { useChatActivity } from './state/ChatActivityContext';
import './header-menu.css';
import { FaBell } from 'react-icons/fa';
import AsyncButton from './components/AsyncButton';
import { useClientSocket } from './state/ClientSocketContext';
import { revokeCurrentWebPushForLogout } from './notifications/web-push-client';

interface HeaderMenuProps {
  showLogo: boolean;
}

const NOTIFICATIONS_POLL_MS = 30_000;

const HeaderMenu: React.FC<HeaderMenuProps> = ({ showLogo }) => {
  // Initialize dropdown to closed state
  const [showDropdown, setShowDropdown] = useState(false);
  const [teams, setTeams] = useState<ApiTeamPost201Response[]>([]);
  const [unviewedCount, setUnviewedCount] = useState(0);
  const logoutPendingRef = useRef(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const notificationSessionGenerationRef = useRef(0);
  const notificationFetchStateRef = useRef({
    generation: 0,
    inFlight: false,
    queued: false,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoggedIn, setUser, setLoggedIn, logout, fetchUserData } =
    useUserContext();
  const { teamApi, authenticationApi, notificationsApi } = useApi();
  const { subscribe } = useClientSocket();
  const notificationSessionKey = isLoggedIn
    ? String(user?.id ?? user?.email ?? 'authenticated')
    : 'logged-out';

  // Ensure dropdowns are closed when location changes (navigation)
  useEffect(() => {
    setShowDropdown(false);
  }, [location.pathname]);

  // Ensure dropdowns are closed when login state changes
  useEffect(() => {
    setShowDropdown(false);
  }, [isLoggedIn]);

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const isConsolePage = location.pathname.startsWith('/console');
  const isAccountPage = location.pathname === '/console/account';

  useEffect(() => {
    const checkUser = async () => {
      try {
        const userData = await fetchUserData();
        if (userData) {
          setLoggedIn(true);
        }
      } catch (error) {
        setLoggedIn(false);
        console.error('Failed to check user status:', error);
      }
    };
    checkUser();
  }, [fetchUserData, setLoggedIn]);

  useEffect(() => {
    const loadTeams = async () => {
      if (isLoggedIn) {
        try {
          // Use TeamApi to get teams
          const response = await teamApi.apiTeamsGet();
          setTeams(response.data);
        } catch (error) {
          console.error('Failed to load teams:', error);
        }
      }
    };
    loadTeams();
  }, [isLoggedIn, teamApi]);

  // Realtime bell: the shared activity socket pushes the bell count
  // (chat mentions are surfaced on the chat launcher instead, so they are
  // excluded here). The 30s poll below is the fallback when the socket is
  // down (reconnecting, proxy hiccup).
  const { bellUnread, connected: activityConnected } = useChatActivity();
  useEffect(() => {
    if (activityConnected) setUnviewedCount(bellUnread);
  }, [bellUnread, activityConnected]);

  const loadNotifications = useCallback(
    async (generation: number) => {
      try {
        const response = await notificationsApi.apiNotificationsGet();
        if (generation !== notificationSessionGenerationRef.current) return;
        setUnviewedCount(response.data.unviewedCount || 0);
      } catch (error) {
        if (generation === notificationSessionGenerationRef.current) {
          console.error('Failed to load notifications:', error);
        }
      }
    },
    [notificationsApi],
  );

  const fetchNotifications = useCallback(async () => {
    const generation = notificationSessionGenerationRef.current;
    let state = notificationFetchStateRef.current;
    if (state.generation !== generation) {
      state = { generation, inFlight: false, queued: false };
      notificationFetchStateRef.current = state;
    }
    if (state.inFlight) {
      state.queued = true;
      return;
    }
    state.inFlight = true;
    try {
      do {
        state.queued = false;
        await loadNotifications(generation);
      } while (
        state.queued &&
        generation === notificationSessionGenerationRef.current
      );
    } finally {
      state.inFlight = false;
    }
  }, [loadNotifications]);

  useEffect(() => {
    const generation = notificationSessionGenerationRef.current + 1;
    notificationSessionGenerationRef.current = generation;
    notificationFetchStateRef.current = {
      generation,
      inFlight: false,
      queued: false,
    };
  }, [notificationSessionKey]);

  useEffect(() => {
    if (!isLoggedIn) return;
    void fetchNotifications();
    const interval = setInterval(fetchNotifications, NOTIFICATIONS_POLL_MS);
    const handleChanged = () => void fetchNotifications();
    window.addEventListener('notifications-changed', handleChanged);
    window.addEventListener('notification-preferences-changed', handleChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notifications-changed', handleChanged);
      window.removeEventListener(
        'notification-preferences-changed',
        handleChanged,
      );
    };
  }, [
    isLoggedIn,
    fetchNotifications,
    location.pathname,
    notificationSessionKey,
  ]);

  useEffect(() => {
    if (!isLoggedIn) return;
    return subscribe((frame) => {
      if (frame.ch === 'activity' || frame.ch === 'approvals') {
        void fetchNotifications();
      }
    });
  }, [fetchNotifications, isLoggedIn, subscribe]);

  const handleLogout = async () => {
    if (logoutPendingRef.current) return;
    let navigatingToIdentityProvider = false;
    try {
      logoutPendingRef.current = true;
      setLogoutPending(true);
      await revokeCurrentWebPushForLogout().catch(() => undefined);
      if (user?.loginMethod === UserResponseLoginMethodEnum.Saml) {
        // A real top-level navigation, not an XHR: the point is for the
        // browser to actually land on the IdP's own sign-out page. The
        // backend endpoint ends the local session unconditionally before
        // it ever attempts that redirect, so this is safe even if the
        // IdP is unreachable (backend/src/rest/rest-sso-saml.ts).
        navigatingToIdentityProvider = true;
        window.location.href = '/api/sso/saml/logout';
        return;
      }
      await authenticationApi.apiAuthenticationLogoutPost();
      logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      logoutPendingRef.current = false;
      setLogoutPending(false);
      if (!navigatingToIdentityProvider) {
        navigate('/login');
      }
    }
  };

  return (
    <>
      <nav className="header-menu">
        {showLogo && (
          <div className="logo" onClick={() => navigate('/')}>
            Delegate
          </div>
        )}

        <div className="nav-items">
          {isLoggedIn ? (
            <div className="user-menu">
              {(!isConsolePage || isAccountPage) && (
                <button
                  className="lightButton"
                  onClick={() => navigate('/console')}>
                  Console
                </button>
              )}

              {/* Notifications bell */}
              {isConsolePage && !isAccountPage && (
                <button
                  className="notifications-bell"
                  data-testid="notifications-bell"
                  title={
                    unviewedCount > 0
                      ? `${unviewedCount} new notification${unviewedCount === 1 ? '' : 's'}`
                      : 'Notifications'
                  }
                  onClick={() => navigate('/console/notifications')}>
                  <FaBell size={16} />
                  {unviewedCount > 0 && (
                    <span
                      className="notifications-badge"
                      data-testid="notifications-badge">
                      {unviewedCount > 99 ? '99+' : unviewedCount}
                    </span>
                  )}
                </button>
              )}

              <button
                id="user-menu-button"
                className="lightButton"
                disabled={logoutPending}
                onClick={toggleDropdown}>
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.email || 'User'}
              </button>

              {showDropdown && (
                <div className="dropdown-menu">
                  <Link
                    to="/console/account"
                    className="dropdown-item"
                    onClick={() => setShowDropdown(false)}>
                    Account Settings
                  </Link>

                  <Link
                    to="/console/notifications"
                    className="dropdown-item"
                    onClick={() => setShowDropdown(false)}>
                    Notifications
                  </Link>

                  <Link
                    to="/console/teams"
                    className="dropdown-item"
                    onClick={() => setShowDropdown(false)}>
                    Manage Teams
                  </Link>

                  <AsyncButton
                    className="dropdown-item logout-button"
                    loading={logoutPending}
                    loadingText="Logging out..."
                    onClick={handleLogout}>
                    Logout
                  </AsyncButton>
                </div>
              )}
            </div>
          ) : (
            location.pathname !== '/login' &&
            location.pathname !== '/create-account' && (
              <button
                className="lightButton"
                onClick={() => navigate('/login')}>
                Log in
              </button>
            )
          )}
        </div>
      </nav>
    </>
  );
};

export default HeaderMenu;
